import React, { useState } from "react";
import { FaTimes, FaExclamationTriangle } from "react-icons/fa";
import "./UnifiedIocValidator.css";
import { useAnalysisResult } from "../../context/AnalysisResultContext";

const verdictLabel = {
  malicious: "Malicious",
  suspicious: "Suspicious",
  harmless: "Harmless",
  unknown: "Unknown / No Data",
};

const verdictHint = {
  malicious: "Multiple providers flag this indicator as malicious.",
  suspicious: "Some risk indicators present. Treat with caution.",
  harmless: "No significant issues reported by OSINT providers.",
  unknown: "OSINT sources did not return enough data.",
};

const UnifiedIocValidator = () => {
  const {
    iocInput: ioc,
    setIocInput: setIoc,
    iocResult: result,
    setIocResult: setResult,
    clearIocResult,
  } = useAnalysisResult();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);

    const trimmed = ioc.trim();
    if (!trimmed) {
      setError("Please enter an IP, domain, URL, hash or email.");
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      const resp = await fetch("/api/unified-ioc/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ioc: trimmed }),
      });

      const data = await resp.json();
      if (!resp.ok || !data.ok) {
        setError(data.error || "Failed to validate IoC.");
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error(err);
      setError("Network error while contacting the IoC validator.");
    } finally {
      setLoading(false);
    }
  };

  const overallClass =
    result?.overall?.color === "danger"
      ? "ioc-overall-danger"
      : result?.overall?.color === "warning"
      ? "ioc-overall-warning"
      : result?.overall?.color === "success"
      ? "ioc-overall-success"
      : "ioc-overall-neutral";

  const overview = result?.overview || null;
  const type = result?.type;
  const isIp = type === "ip";
  const isDomainLike = type === "domain" || type === "url";
  const isInternal = result?.overall?.internal;

  const renderValue = (val) =>
    val && Array.isArray(val) ? val.join(", ") : val || "Not available";

  return (
    <div className="ioc-page">
      <div className="ioc-card">
        <div className="ioc-card-header">
          <h2>Unified IoC Validator</h2>
          <p>
            Validate IPs, domains, URLs, hashes and emails across OSINT
            providers in one shot.
          </p>
        </div>

        <form className="ioc-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="ioc-input"
            placeholder="Paste an IP, domain, URL, hash or mail ID"
            value={ioc}
            onChange={(e) => setIoc(e.target.value)}
            disabled={loading}
          />
          <button
            className="pmq-neon-btn"
            type="submit"
            disabled={loading}
          >
            {loading ? "Analyzing..." : "Analyze IoC"}
          </button>
        </form>

        {error && <div className="ioc-error">{error}</div>}

        {result && (
          <div className={`ioc-overall-card ${overallClass}`}>
            <div className="ioc-overall-header">
              <h3>AI Overview</h3>
              <div className="ioc-overall-header-right">
                <span
                  className={`ioc-overall-chip ioc-verdict-${result.overall.verdict || "unknown"}`}
                >
                  {verdictLabel[result.overall.verdict] || "Unknown"}
                </span>
                <button
                  type="button"
                  className="ioc-clear-btn"
                  onClick={() => { clearIocResult(); setError(""); }}
                  title="Clear result"
                  aria-label="Clear result"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <p className="ioc-overall-ioc">
              <span>Indicator:</span> {result.ioc}{" "}
              <span className="ioc-type-pill">
                {result.type}
                {isInternal && (
                  <span className="ioc-type-pill-secondary">Internal</span>
                )}
              </span>
            </p>

            <p className="ioc-overall-text">
              {result.overall.insight ||
                result.overall.internalNote ||
                result.overall.reason ||
                verdictHint[result.overall.verdict]}
            </p>

            {result.overall.rateLimited &&
              Array.isArray(result.overall.rateLimitedProviders) && (
                <div className="ioc-ratelimit-banner" role="status">
                  <FaExclamationTriangle aria-hidden="true" />
                  <span>
                    Rate limit reached for{" "}
                    <strong>
                      {result.overall.rateLimitedProviders
                        .map((p) => p.toUpperCase())
                        .join(", ")}
                    </strong>
                    . Their intelligence is missing from this result — re-run in a
                    little while for full coverage.
                  </span>
                </div>
              )}
{isDomainLike &&
  typeof result.overall.domainAgeDays === "number" && (
    <p className="ioc-domain-age">
      Domain age:{" "}
      <strong>{result.overall.domainAgeDays} days</strong>
      {result.overall.domainAgeLabel && (
        <>
          {" "}
          <span className="ioc-domain-age-label">
            ({result.overall.domainAgeLabel})
          </span>
        </>
      )}
      {result.overall.isNewDomain && (
        <span className="ioc-domain-age-badge ioc-domain-age-new">
          New domain (&lt; 30 days)
        </span>
      )}
    </p>
  )}


            <p className="ioc-overall-score">
              Unified risk score:{" "}
              <strong>{result.overall.score ?? 0} / 100</strong>
            </p>

            {/* ===== IoC Analysis Overview (table) ===== */}
            {overview && (
              <div className="ioc-intel-card">
                <div className="ioc-intel-header">
                  <h3>IoC Analysis Overview</h3>
                  <p>
                    High-level profile of the indicator based on WHOIS, DNS and
                    IP intelligence.
                  </p>
                </div>

                {/* IP table */}
                {isIp && (
                  <div className="ioc-table-wrap">
                  <table className="ioc-intel-table">
                    <tbody>
                      <tr>
                        <th>IP Address</th>
                        <td>{renderValue(overview.ip)}</td>
                      </tr>
                      <tr>
                        <th>Location</th>
                        <td>{renderValue(overview.location)}</td>
                      </tr>
                      <tr>
                        <th>Country</th>
                        <td>{renderValue(overview.country)}</td>
                      </tr>
                      <tr>
                        <th>City</th>
                        <td>{renderValue(overview.city)}</td>
                      </tr>
                      <tr>
                        <th>Organization</th>
                        <td>{renderValue(overview.org)}</td>
                      </tr>
                      <tr>
                        <th>ISP</th>
                        <td>{renderValue(overview.isp)}</td>
                      </tr>
                      <tr>
                        <th>ASN</th>
                        <td>{renderValue(overview.asn)}</td>
                      </tr>
                      <tr>
                        <th>Domain Name</th>
                        <td>{renderValue(overview.domainName)}</td>
                      </tr>
                      <tr>
                        <th>Network (CIDR)</th>
                        <td>
                          {overview.networkCidr
                            ? `${overview.networkCidr}${
                                overview.networkName
                                  ? ` – ${overview.networkName}`
                                  : ""
                              }`
                            : "Not available"}
                        </td>
                      </tr>
                      {Array.isArray(overview.reverseDns) &&
                        overview.reverseDns.length > 0 && (
                          <tr>
                            <th>Reverse DNS</th>
                            <td>{overview.reverseDns.join(", ")}</td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                  </div>
                )}

                {/* Domain / URL table */}
                {isDomainLike && (
                  <table className="ioc-intel-table">
                    <tbody>
                      <tr>
                        <th>Domain</th>
                        <td>{renderValue(overview.domain)}</td>
                      </tr>
                      <tr>
                        <th>Resolved IP</th>
                        <td>{renderValue(overview.ipAddress)}</td>
                      </tr>
                      <tr>
                        <th>Location</th>
                        <td>{renderValue(overview.location)}</td>
                      </tr>
                      <tr>
                        <th>Country</th>
                        <td>{renderValue(overview.country)}</td>
                      </tr>
                      <tr>
                        <th>City</th>
                        <td>{renderValue(overview.city)}</td>
                      </tr>
                      <tr>
                        <th>Registrar</th>
                        <td>{renderValue(overview.registrar)}</td>
                      </tr>
                      <tr>
                        <th>Registry Domain ID</th>
                        <td>{renderValue(overview.registryDomainId)}</td>
                      </tr>
                      <tr>
                        <th>Registered On</th>
                        <td>{renderValue(overview.registeredOn)}</td>
                      </tr>
                      <tr>
                        <th>Updated On</th>
                        <td>{renderValue(overview.updatedOn)}</td>
                      </tr>
                      <tr>
                        <th>Expires On</th>
                        <td>{renderValue(overview.expiresOn)}</td>
                      </tr>
                      {Array.isArray(overview.statuses) &&
                        overview.statuses.length > 0 && (
                          <tr>
                            <th>Domain Status</th>
                            <td>{overview.statuses.join(", ")}</td>
                          </tr>
                        )}
                      {Array.isArray(overview.nameServers) &&
                        overview.nameServers.length > 0 && (
                          <tr>
                            <th>Name Servers</th>
                            <td>{overview.nameServers.join(", ")}</td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                )}

                {/* Optional little nudge for internal IPs */}
                {isIp && isInternal && (
                  <p className="ioc-internal-tip">
                    This address is in an internal / private range. Correlate
                    with your internal network design and asset inventory before
                    taking perimeter block actions.
                  </p>
                )}
              </div>
            )}

            {/* ===== Provider cards ===== */}
            <div className="ioc-provider-grid">
              {Object.entries(result.providers || {}).map(
                ([name, provider]) => (
                  <div key={name} className="ioc-provider-card">
                    <div className="ioc-provider-header">
                      <span className="ioc-provider-name">
                        {name.toUpperCase()}
                      </span>
                      {!provider.enabled ? (
                        <span className="ioc-provider-disabled">
                          Disabled / No key
                        </span>
                      ) : provider.rateLimited ? (
                        <span className="ioc-provider-ratelimited">
                          <FaExclamationTriangle aria-hidden="true" />
                          Rate limited
                        </span>
                      ) : (
                        <span
                          className={`ioc-provider-verdict ioc-provider-verdict-${
                            provider.verdict || "unknown"
                          }`}
                        >
                          {verdictLabel[provider.verdict] || "Unknown"}
                        </span>
                      )}
                    </div>

                    {provider.enabled && (
                      <>
                        <p className="ioc-provider-detail">
                          {provider.detail || "No additional details."}
                        </p>

                        {typeof provider.score === "number" && (
                          <p className="ioc-provider-score">
                            Score:{" "}
                            <strong>
                              {Math.round(provider.score)} /{" "}
                              {provider.scoreMax ?? 100}
                            </strong>
                          </p>
                        )}

                        {provider.link && (
                          <a
                            href={provider.link}
                            target="_blank"
                            rel="noreferrer"
                            className="ioc-provider-link"
                          >
                            View in portal
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedIocValidator;
